import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {

    this._inuraInit = true;


    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Fin d'init après 2 frames (après mesures/reflow)
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    this._inuraInit = false;
  });
});


  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

/* ===== INURA PATCH CUSTOM START =====
   Accordion: single-open + scroll-to-heading (USER CLICK ONLY)
   - Ne réagit qu'à un clic humain (pas au load, pas au back/refresh)
===== */

(function () {
  const prefersReduced = () =>
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getStickyHeaderHeight() {
    const header =
      document.querySelector('[data-header-wrapper]') ||
      document.querySelector('.header-wrapper') ||
      document.querySelector('header');

    if (!header) return 0;

    const cs = window.getComputedStyle(header);
    return cs.position === 'sticky' || cs.position === 'fixed'
      ? Math.ceil(header.getBoundingClientRect().height)
      : 0;
  }

  function closeSiblings(detailsEl) {
    const accordion = detailsEl.closest('.accordion');
    if (!accordion) return;

    accordion.querySelectorAll('details[open]').forEach((other) => {
      if (other !== detailsEl) other.removeAttribute('open');
    });
  }

  function scrollToHeading(detailsEl) {
    const summary = detailsEl.querySelector('summary') || detailsEl;
    const headerHeight = getStickyHeaderHeight();
    const extraSpacing = 20;

    const rect = summary.getBoundingClientRect();
    let top = rect.top + window.pageYOffset - headerHeight - extraSpacing;

    const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    top = Math.min(Math.max(0, top), maxY);

    window.scrollTo({
      top,
      behavior: prefersReduced() ? 'auto' : 'smooth'
    });
  }

  // ✅ On écoute le clic sur SUMMARY uniquement (donc user action seulement)
  document.addEventListener(
    'click',
    (e) => {
      const summary = e.target.closest('summary');
      if (!summary) return;

      // Trouver le details parent
      const detailsEl = summary.closest('details');
      if (!(detailsEl instanceof HTMLDetailsElement)) return;

      // On ne gère que les details qui appartiennent à un <accordion-custom>
      const component = summary.closest('accordion-custom');
      if (!component) return;

      // Bloque pendant l'init du composant (évite load/reflow)
      if (component._inuraInit) return;

      // Si le composant est "disabled" via tes data-* (mobile/desktop), ne rien faire
      // (On ne peut pas lire ses #private fields, donc on respecte son preventDefault :
      // si le thème a preventDefault, on sort)
      if (e.defaultPrevented) return;

      // Ferme les autres rubriques du même groupe
      closeSiblings(detailsEl);

      // Après le toggle natif du browser, scroll vers le heading
      detailsEl.addEventListener(
        'toggle',
        () => {
          if (!detailsEl.open) return;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => scrollToHeading(detailsEl));
          });
        },
        { once: true }
      );
    },
    true
  );
})();
/**=================INURA PATCH END==============================/ */